import { Button, Icon, Popover, PopoverContent, PopoverTrigger, Tooltip } from "@sarva/ui";
import { useDensity, type Density } from "@/app/theme";

/**
 * The row-density preference, in the topnav — because doc 40 §10 specifies one and nothing
 * in the application reached it.
 *
 * ## Why this was the largest single win in the design audit
 *
 * Doc 40 §10 defines three modes and says density "is a per-user preference persisted
 * server-side". The tokens were there (`--row-height` 48/40/32), `ThemeProvider` implemented
 * `setDensity`, `data-density` was already on the root element — and **no component in the
 * app called it**, so every user was locked to the `compact` default and the `dense` mode the
 * spec designed for "posting, remittance lines, bulk review" could not be selected at all.
 *
 * The measured consequence, on `/queue` at 1440×900 after the chrome above the table was
 * reclaimed: `comfortable` shows 13 rows, `compact` 16, `dense` **20**. A 20-row page fits
 * without a single scroll gesture — but only in the mode nobody could choose.
 * (docs/30-design/42_DESIGN_AUDIT.md, D7.)
 *
 * ## Why the topnav and not a table control
 *
 * It applies to every table on every screen, so it is an application preference of exactly
 * the same kind as the theme — not a property of the grid you happen to be looking at. A
 * per-table control would also imply the choice is per-table, which would be a lie: the
 * attribute lives on `<html>`.
 *
 * **Not persisted yet, and that is a gap rather than a decision.** Doc 40 §10 says "persisted
 * server-side"; `/me` carries no such field and no endpoint accepts one, so the choice lasts
 * as long as the tab. Recorded in the audit rather than papered over with `localStorage`,
 * which would persist it on one machine and quietly diverge from the spec's promise.
 */

/** Label, and the reason to pick it — the row height is the honest description. */
const MODES: ReadonlyArray<{ value: Density; label: string; hint: string }> = [
  { value: "comfortable", label: "Comfortable", hint: "48px rows — detail views and forms" },
  { value: "compact", label: "Compact", hint: "40px rows — the default for queues" },
  { value: "dense", label: "Dense", hint: "32px rows — bulk review, most rows per screen" },
];

export function DensityMenu() {
  const { density, setDensity } = useDensity();
  const current = MODES.find((mode) => mode.value === density) ?? MODES[1];

  return (
    <Popover>
      <Tooltip content={`Row density: ${current?.label ?? "Compact"}`} side="bottom">
        <PopoverTrigger asChild>
          <Button variant="ghost" size="md" iconOnly aria-label="Row density">
            <Icon name="columns" size={4} />
          </Button>
        </PopoverTrigger>
      </Tooltip>
      <PopoverContent align="end" className="min-w-dropdown">
        <p className="mb-2 text-2xs font-medium uppercase text-secondary">Row density</p>
        <ul className="flex flex-col gap-0.5">
          {MODES.map((mode) => (
            <li key={mode.value}>
              <button
                type="button"
                onClick={() => setDensity(mode.value)}
                aria-current={mode.value === density}
                className={[
                  "flex w-full items-start gap-2 rounded-sm px-2 py-1.5 text-left",
                  "hover:bg-subtle focus-visible:shadow-focus-ring focus-visible:outline-none",
                ].join(" ")}
              >
                {/* The check occupies its slot whether or not it is shown, so the labels do
                    not shift as the selection moves. */}
                <span className="mt-0.5 w-4 shrink-0">
                  {mode.value === density ? <Icon name="check" size={4} /> : null}
                </span>
                <span className="flex flex-col">
                  <span className="text-sm text-default">{mode.label}</span>
                  <span className="text-xs text-tertiary">{mode.hint}</span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
